import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { Typography, Space, Tag, Row, Col, Card, Table } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { LineChart, Line, Tooltip, ResponsiveContainer } from "recharts";
import { ValueFormatter } from "src/utils/formatters";
import { stripMarkdown } from "../cardUtils";

interface TrendData {
  metric: string;
  group: string;
  time_period: string;
  values: number[];
  commentary: string;
  additional_info?: string;
  sample_size?: number;
  metric_unit?: string;
}

// TrendAnalysis Block Recipe
const trendAnalysisRecipe = {
  id: "trend-analysis-block",
  name: "Trend Analysis Block",
  globalKey: [],
  prefix: `[
    {
      "metric": "string",
      "group": "string",
      "time_period": "string",
      "values": [0],
      "commentary": "string",
      "additional_info": "string — optional",
      "sample_size": "number — optional",
      "metric_unit": "string — optional"
    }
  ]`,
  format: {
    type: "json",
    fields: ["metric", "group", "values", "commentary"],
    constraints: [
      { field: "metric", type: "string", required: true },
      { field: "group", type: "string", required: true },
      { field: "values", type: "array", required: true },
      { field: "commentary", type: "string", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as TrendData[];
          return (
            Array.isArray(parsed) &&
            parsed.every(
              (item) =>
                item.metric &&
                item.group &&
                Array.isArray(item.values) &&
                item.commentary
            )
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// TrendAnalysis Block Parser
function trendAnalysisParser(data: any) {
  try {
    return {
      data: data[trendAnalysisBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse trend analysis data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// TrendAnalysis Block Variants
const trendAnalysisVariants = [
  {
    id: "default",
    name: "Default",
    description:
      "Full details: metric, group, time period, values, commentary, additional info, sample size, metric unit",
    defaultSettings: {},
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Metric, group, sparkline/last value, short summary",
    defaultSettings: {},
  },
  {
    id: "modern",
    name: "Modern",
    description: "Emphasize trend visually, compact layout",
    defaultSettings: {},
  },
  {
    id: "table",
    name: "Table",
    description: "Tabular view for quick comparison",
    defaultSettings: {},
  },
];

// TrendAnalysis Block Renderer
function TrendAnalysisBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const trendsData = data[trendAnalysisBlock.id];
  const variantSettings = trendAnalysisVariants.find(
    (v) => v.id === variant
  )?.defaultSettings;

  if (!trendsData || trendsData.length === 0) return null;

  // Variant renderers
  const renderDefaultCard = (item: TrendData) => (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: "space-between",
        }}
      >
        <Typography.Text className="vzbl-header-5">
          {stripMarkdown(item.group)}
        </Typography.Text>
        <Typography.Text className="vzbl-text-3">
          <strong>Sample Size:</strong> {item.sample_size}
        </Typography.Text>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: "flex-start",
        }}
      >
        <Typography.Text className="vzbl-text-3">
          <strong>Unit:</strong> {item.metric_unit}
        </Typography.Text>
        <Typography.Text className="vzbl-text-3">
          <strong>Time Period:</strong> {item.metric}
        </Typography.Text>
      </div>

      <div style={{ height: 100, padding: "var(--padding-3) 0" }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={item.values.map((value: number, idx: number) => ({
              index: idx,
              value: value,
            }))}
            margin={{ top: 10, right: 0, bottom: 20, left: 0 }}
          >
            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--color-primary)"
              strokeWidth={2}
              dot={{ r: 4, fill: "var(--color-primary)" }}
              activeDot={{ r: 6 }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (
                  active &&
                  payload &&
                  payload.length > 0 &&
                  typeof payload[0].value === "number"
                ) {
                  return (
                    <div
                      style={{
                        backgroundColor: "var(--color-bg-elevated)",
                        padding: "var(--padding-3)",
                        border: "1px solid var(--color-border)",
                        borderRadius: "var(--border-radius)",
                      }}
                    >
                      <Typography.Text className="vzbl-text-3">
                        {ValueFormatter.formatWithUnit(
                          payload[0].value,
                          item.metric
                        )}
                      </Typography.Text>
                    </div>
                  );
                }
                return null;
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {item.commentary && (
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {item.commentary}
        </Markdown>
      )}
      {item.additional_info && (
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {item.additional_info}
        </Markdown>
      )}
    </Space>
  );

  const renderMinimalCard = (item: TrendData) => (
    <Space direction="vertical" size={2} style={{ width: "100%" }}>
      <Typography.Text className="vzbl-header-5">
        {stripMarkdown(item.group)}
      </Typography.Text>
      <Typography.Text className="vzbl-text-3">
        <strong>Last:</strong> {item.values.at(-1)} {item.metric_unit}
      </Typography.Text>
      {item.commentary && (
        <Typography.Text
          className="vzbl-text-3"
          style={{ color: "var(--color-text-secondary)" }}
        >
          <Markdown
            remarkPlugins={[remarkGfm]}
            skipHtml={true}
            className="vzbl-text-3"
          >
            {item.commentary}
          </Markdown>
        </Typography.Text>
      )}
    </Space>
  );

  const renderModernCard = (item: TrendData) => (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: "space-between",
        }}
      >
        <Typography.Text className="vzbl-header-5">
          {stripMarkdown(item.group)}
        </Typography.Text>
        <Tag className="vzbl-pill-tag">{stripMarkdown(item.metric)}</Tag>
      </div>
      <Typography.Text className="vzbl-text-3">
        <strong>Last:</strong> {item.values.at(-1)} {item.metric_unit}
      </Typography.Text>
      {item.commentary && (
        <Typography.Text
          className="vzbl-text-3"
          style={{ color: "var(--color-text-secondary)" }}
        >
          <Markdown
            remarkPlugins={[remarkGfm]}
            skipHtml={true}
            className="vzbl-text-3"
          >
            {item.commentary}
          </Markdown>
        </Typography.Text>
      )}
    </Space>
  );

  const renderTable = (data: TrendData[]) => {
    const columns = [
      { title: "Group", dataIndex: "group", key: "group" },
      { title: "Metric", dataIndex: "metric", key: "metric" },
      { title: "Time Period", dataIndex: "time_period", key: "time_period" },
      {
        title: "Last Value",
        dataIndex: "values",
        key: "last_value",
        render: (values: number[]) => values.at(-1),
      },
      { title: "Unit", dataIndex: "metric_unit", key: "metric_unit" },
      { title: "Sample Size", dataIndex: "sample_size", key: "sample_size" },
    ];
    return (
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        rowKey={(r) => r.group + r.metric}
        size="small"
      />
    );
  };

  // Main renderer for all variants
  const getCardRenderer = () => {
    switch (variant) {
      case "minimal": {
        return renderMinimalCard;
      }
      case "modern": {
        return renderModernCard;
      }
      default: {
        return renderDefaultCard;
      }
    }
  };
  const renderCard = getCardRenderer();

  if (variant === "table") {
    return (
      <div className="vzbl-panel-container">
        <Typography.Title level={4} className="vzbl-header-4">
          Trend Analysis
        </Typography.Title>
        {renderTable(trendsData)}
      </div>
    );
  }

  return (
    <div className="vzbl-panel-container">
      <Typography.Title level={4} className="vzbl-header-4">
        Trend Analysis
      </Typography.Title>
      <div className="responsive-card-grid">
        {trendsData.map((item: TrendData, idx: number) => (
          <div
            className="vzbl-panel-shadow vzbl-panel-overlay"
            key={`trend-${idx}`}
          >
            {renderCard(item)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Create TrendAnalysis Block
const trendAnalysisBlock = createBaseBlock({
  id: "trend-analysis",
  name: "Trend Analysis Block",
  description: "Displays trend analysis with charts and insights",
  recipe: trendAnalysisRecipe,
  variants: trendAnalysisVariants,
  parser: trendAnalysisParser,
  renderer: TrendAnalysisBlockRenderer,
});

// Export the block for direct use
export { trendAnalysisBlock };
